'use client';

import styles from './pagination.module.css';
import clsx from 'clsx';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { JSX, memo, useCallback, useEffect, useState } from 'react';
import { Flex, IconButton } from '@radix-ui/themes';
import {
  CaretLeftIcon,
  CaretRightIcon,
  DotsHorizontalIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon
} from '@radix-ui/react-icons';
import { useAppSelector } from '@/app/lib/store/hooks';

interface ArrowIconButtonProps {
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  icon: JSX.Element;
  testid?: string;
}

const MemoizedArrowIconButton = memo(
  ({
    onClick,
    onMouseEnter,
    onMouseLeave,
    icon,
    testid
  }: ArrowIconButtonProps) => {
    return (
      <div
        className={styles.ArrowIconButton}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid={testid}
      >
        {icon}
      </div>
    );
  }
);
MemoizedArrowIconButton.displayName = 'MemoizedArrowIconButton';

interface PageButtonProps {
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

export function PageButton({ active, onClick, children }: PageButtonProps) {
  return (
    <div
      className={clsx(styles.PageButton, {
        [styles.PageButtonActive]: active,
        [styles.PageButtonInactive]: !active
      })}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function usePageRange(
  total: number,
  current: number,
  isMobile: boolean | undefined
) {
  const [range, setRange] = useState<number[]>([]);
  // const [rangeSize, setRangeSize] = useState(isMobile ? 3 : 5);
  const rangeSize = isMobile ? 3 : 5;

  const [showLeftArrow, setShowLeftArrow] = useState(current > 4);
  const [showRightArrow, setShowRightArrow] = useState(current < total - 5);

  const configRange = useCallback(() => {
    const pages = Array.from({ length: total - 2 }, (_, i) => i + 2);

    const slicePages = (pages: number[], page: number) => {
      if (pages.length <= rangeSize) return pages;

      if (page < rangeSize) return pages.slice(0, rangeSize);
      if (page > total - rangeSize) return pages.slice(-rangeSize);

      const half = Math.floor(rangeSize / 2);
      return pages.slice(page - half - 1, page + half);
    };

    const displayPages = slicePages(pages, current);
    setRange(displayPages);

    setShowLeftArrow(displayPages[0] > 2);
    setShowRightArrow(displayPages[displayPages.length - 1] < total - 1);
  }, [total, current, rangeSize]);
  useEffect(() => {
    configRange();
  }, [total, current, configRange]);

  return { range, showLeftArrow, showRightArrow };
}

interface Props {
  mobile?: boolean;
}

export default function Pagination({ mobile }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  //Предотвращаем проблему с гидратацией
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const total = useAppSelector(state => state.totalPages.count);

  const storedSearchParams = useAppSelector(
    state => state.searchParams.params,
    (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
  );

  const [current, setCurrent] = useState(Number(storedSearchParams.page) || 1);

  const { range, showLeftArrow, showRightArrow } = usePageRange(
    total,
    Number(storedSearchParams.page) || 1,
    mobile
  );

  const [hoverLeft, setHoverLeft] = useState(false);
  const [hoverRight, setHoverRight] = useState(false);

  const handleChange = (page: number) => {
    const createPageURL = (pageNumber: number | string) => {
      const params = new URLSearchParams(searchParams);

      params.set('page', pageNumber.toString());

      return `${pathname}?${params.toString()}`;
    };
    setCurrent(page);
    router.push(createPageURL(page));
    // router.push(createPageURL(page), { scroll: false });
  };

  const handleNext = () => {
    if (current < total) {
      const newPage = current + 1;
      handleChange(newPage);
    }
  };

  const handleFastForward = () => {
    if (current <= total - 5) {
      const newPage = current + 5;
      handleChange(newPage);
    }
  };

  const handlePrevious = () => {
    if (current > 1) {
      const newPage = current - 1;
      handleChange(newPage);
    }
  };

  const handleFastBackward = () => {
    if (current >= 5) {
      const newPage = current - 5;
      handleChange(newPage);
    }
  };

  const goToPage = (page: number) => {
    handleChange(page);
  };

  useEffect(() => {
    setCurrent(Number(storedSearchParams.page) || 1);
  }, [storedSearchParams]);

  if (!isClient) return null;

  return (
    <Flex
      gap='2'
      align='center'
      justify='center'
      mt='4'
    >
      <IconButton
        disabled={current === 1}
        onClick={handlePrevious}
        variant='ghost'
        className={styles.StyledIconButton}
        data-testid='previous-button'
      >
        <CaretLeftIcon
          width='1.5rem'
          height='1.5rem'
        />
      </IconButton>

      {/* Page numbers */}
      <Flex
        align='center'
        gap='2'
      >
        <Flex
          justify='center'
          align='center'
          width='34px'
          height='34px'
        >
          <PageButton
            onClick={() => goToPage(1)}
            active={current === 1}
          >
            1
          </PageButton>
        </Flex>
        {showLeftArrow && (
          <MemoizedArrowIconButton
            onClick={handleFastBackward}
            onMouseEnter={() => showLeftArrow && setHoverLeft(true)}
            onMouseLeave={() => showLeftArrow && setHoverLeft(false)}
            icon={
              hoverLeft ? (
                <DoubleArrowLeftIcon
                  width='1rem'
                  height='1rem'
                  data-testid='double-arrows-left-icon'
                />
              ) : (
                <DotsHorizontalIcon
                  width='1rem'
                  height='1rem'
                  data-testid='dots-icon-backward-button'
                />
              )
            }
            testid='fast-backward-button'
          />
        )}
        {range.map(page => (
          <Flex
            key={page}
            justify='center'
            align='center'
            width='34px'
            height='34px'
          >
            <PageButton
              onClick={() => goToPage(page)}
              active={current === page}
            >
              {page}
            </PageButton>
          </Flex>
        ))}
        {showRightArrow && (
          <MemoizedArrowIconButton
            onClick={handleFastForward}
            onMouseEnter={() => showRightArrow && setHoverRight(true)}
            onMouseLeave={() => showRightArrow && setHoverRight(false)}
            icon={
              hoverRight ? (
                <DoubleArrowRightIcon
                  width='1rem'
                  height='1rem'
                  data-testid='double-arrows-right-icon'
                />
              ) : (
                <DotsHorizontalIcon
                  width='1rem'
                  height='1rem'
                  data-testid='dots-icon-forward-button'
                />
              )
            }
            testid='fast-forward-button'
          />
        )}
        {total > 1 && (
          <Flex
            justify='center'
            align='center'
            width='34px'
            height='34px'
          >
            <PageButton
              onClick={() => goToPage(total)}
              active={current === total}
            >
              {total}
            </PageButton>
          </Flex>
        )}
      </Flex>

      <IconButton
        disabled={current === total}
        onClick={handleNext}
        variant='ghost'
        className={styles.StyledIconButton}
        data-testid='next-button'
      >
        <CaretRightIcon
          width='1.5rem'
          height='1.5rem'
        />
      </IconButton>
    </Flex>
  );
}
